import { useCallback, useEffect, useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	ComposedChart,
	Bar,
	XAxis,
	YAxis,
	Line,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import { Loading } from "../../../components/common/Loading";
import CustomNetWorthChartTooltip from "./CustomNetWorthChartTooltip";
import { useTranslation } from "react-i18next";

export default function NettWorthGraph({
	period,
	periodType,
	netWorth,
	netWorthLoading,
	categoryKeys,
}) {
	const theme = useTheme();
	const [seriesData, setSeriesData] = useState([]);
	const userCurrency = localStorage.getItem("userCurrency");
	const darkmode = localStorage.getItem("darkMode") === "true";
	const { t } = useTranslation();

	const transformNetWorthData = useCallback(
		(netWorth) => {
			if (!netWorth?.length) return [];

			const sortedData = netWorth.sort(
				(a, b) => new Date(a.date) - new Date(b.date)
			);
			const endDate = new Date(period);
			endDate.setUTCHours(0, 0, 0, 0);

			const startDate = new Date(endDate);
			if (periodType === "Daily") {
				startDate.setUTCMonth(startDate.getUTCMonth() - 1);
				startDate.setUTCDate(startDate.getUTCDate() + 1);
			} else {
				startDate.setUTCFullYear(
					endDate.getUTCFullYear(),
					endDate.getUTCMonth() - 11,
					1
				);
			}
			startDate.setUTCHours(0, 0, 0, 0);

			const result = [];
			const currentDate = new Date(startDate);
			let lastKnownData = null;

			while (currentDate <= endDate) {
				const match = sortedData.find((d) => {
					const dDate = new Date(d.date);
					dDate.setUTCHours(0, 0, 0, 0);

					return periodType === "Daily"
						? dDate.getTime() === currentDate.getTime()
						: dDate.getUTCFullYear() ===
								currentDate.getUTCFullYear() &&
								dDate.getUTCMonth() ===
									currentDate.getUTCMonth();
				});

				const entry = match || lastKnownData;
				if (match) lastKnownData = match;

				result.push({
					date: currentDate.toISOString().split("T")[0],
					netWorth: entry?.netWorth || 0,
					assets: { ...(entry?.assets || {}) },
					liabilities: { ...(entry?.liabilities || {}) },
				});

				if (periodType === "Daily") {
					currentDate.setUTCDate(currentDate.getUTCDate() + 1);
				} else {
					currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
				}
			}
			return result;
		},
		[period, periodType]
	);

	const transformNetWorthToSeriesData = useCallback((filledNetWorthData) => {
		const allKeys = new Set();
		filledNetWorthData.forEach((entry) => {
			Object.keys(entry.assets || {}).forEach((key) => allKeys.add(key));
			Object.keys(entry.liabilities || {}).forEach((key) =>
				allKeys.add(key)
			);
		});

		return filledNetWorthData.map((entry) => {
			const dataPoint = { date: entry.date, netWorth: entry.netWorth };

			allKeys.forEach((key) => {
				const assetValue = entry.assets?.[key] || 0;
				const liabilityValue = entry.liabilities?.[key] || 0;
				dataPoint[key] = assetValue + liabilityValue;
			});

			return dataPoint;
		});
	}, []);

	const maxValue = useMemo(() => {
		if (!seriesData?.length) return 0;

		let maxPositive = 0;
		let maxNegative = 0;

		seriesData.forEach((d) => {
			let positiveSum = 0;
			let negativeSum = 0;

			Object.entries(d).forEach(([key, value]) => {
				if (["date", "netWorth"].includes(key)) return;
				if (value >= 0) positiveSum += value;
				else negativeSum += value;
			});

			maxPositive = Math.max(maxPositive, positiveSum);
			maxNegative = Math.min(maxNegative, negativeSum);
		});

		const maxAbs = Math.max(Math.abs(maxPositive), Math.abs(maxNegative));
		return maxAbs.toFixed(2);
	}, [seriesData]);

	const formatValue = useCallback(
		(value) => {
			return maxValue.toLocaleString().length > 5
				? value.toLocaleString()
				: value.toLocaleString(undefined, {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					});
		},
		[maxValue]
	);

	const yAxisWidth = useMemo(() => {
		if (!seriesData?.length) return 80;

		const ctx = document.createElement("canvas").getContext("2d");
		ctx.font = "17.15px Roboto";

		const formatted = formatValue(maxValue);
		const textWidth = ctx.measureText(formatted).width;
		const padding = maxValue.toLocaleString().length > 5 ? 0 : 10;
		return textWidth + padding;
	}, [seriesData, maxValue, formatValue]);

	useEffect(() => {
		if (netWorth?.length) {
			const transformed = transformNetWorthData(netWorth);
			const series = transformNetWorthToSeriesData(transformed);
			setSeriesData(series);
		}
	}, [netWorth, transformNetWorthData, transformNetWorthToSeriesData]);

	if (netWorthLoading) return <Loading />;

	const formatDate = (value) => {
		const d = new Date(value);
		return periodType === "Monthly"
			? d.toLocaleDateString(undefined, {
					month: "short",
					year: "numeric",
				})
			: d.toLocaleDateString();
	};

	const chartBoxStyle = {
		maxWidth: "100%",
		flexGrow: 1,
		backgroundColor: theme.palette.background.paper,
		m: 2,
		borderRadius: "10px",
		padding: "16px",
	};

	return (
		<Box sx={chartBoxStyle}>
			<Typography
				variant="h6"
				id="tableTitle"
				component="div"
				sx={{ mb: 2 }}
			>
				{t("networthGraph")} ({userCurrency})
			</Typography>
			<ResponsiveContainer width="100%" height={400}>
				<ComposedChart
					data={seriesData}
					stackOffset="sign"
					margin={{ top: 15, right: 5, bottom: 5, left: 5 }}
				>
					<CartesianGrid
						vertical={false}
						strokeDasharray="5 5"
						opacity={darkmode ? 0.5 : 1}
					/>
					<XAxis
						dataKey="date"
						axisLine={false}
						tick={{ fill: theme.palette.text.secondary }}
						tickLine={false}
						interval="preserveStartEnd"
						tickFormatter={formatDate}
					/>
					<YAxis
						tickFormatter={formatValue}
						width={yAxisWidth}
						axisLine={false}
						tickLine={false}
						tick={{ fill: theme.palette.text.secondary }}
					/>
					<Tooltip
						content={(props) => (
							<CustomNetWorthChartTooltip
								{...props}
								categoryKeys={categoryKeys}
								periodType={periodType}
							/>
						)}
					/>
					{categoryKeys.map(({ key, color }) => (
						<Bar key={key} dataKey={key} stackId="a" fill={color} />
					))}
					<Line
						name="Net Worth"
						type="monotone"
						dataKey="netWorth"
						stroke={theme.palette.text.primary}
						strokeWidth={3}
						dot={false}
					/>
				</ComposedChart>
			</ResponsiveContainer>
		</Box>
	);
}
